/**
 * One-tap migration button for legacy attachments.
 *
 * iter-149g: production attachments were on the pod's local disk which
 * gets wiped on every redeploy. New uploads now go to durable storage;
 * this card lets an admin migrate any surviving on-disk files into
 * durable storage from a mobile browser without needing curl / DevTools.
 *
 * iter-158: made the card reusable for either "events" or "chat"
 * attachments — both endpoints follow the same shape (migrated /
 * already_migrated / skipped_missing_file / *_scanned).
 */
import { useState } from "react";
import { toast } from "sonner";
import { apiClient } from "../lib/api";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { HardDriveDownload } from "lucide-react";

const KIND_CONFIG = {
  events: {
    title: "Fix event attachments (one-time)",
    description:
      "Copies any surviving event attachments from this server's local " +
      "disk into durable storage so they survive every future redeploy. " +
      "Safe to run more than once.",
    endpoint: "/events/attachments/migrate-legacy",
    scannedKey: "events_scanned",
    scannedLabel: "events",
    testid: "migrate-attachments",
  },
  chat: {
    title: "Fix chat attachments (one-time)",
    description:
      "Copies any surviving chat files (photos, PDFs) from this server's " +
      "local disk into durable storage so they survive every future " +
      "redeploy. Safe to run more than once.",
    endpoint: "/chat/attachments/migrate-legacy",
    scannedKey: "attachments_scanned",
    scannedLabel: "attachments",
    testid: "migrate-chat-attachments",
  },
};

const MigrateAttachmentsCard = ({ kind = "events" }) => {
  const cfg = KIND_CONFIG[kind] || KIND_CONFIG.events;
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const handleMigrate = async () => {
    setBusy(true);
    try {
      const res = await apiClient.post(cfg.endpoint);
      setResult(res.data);
      const { migrated, already_migrated, skipped_missing_file } = res.data;
      if (migrated === 0 && skipped_missing_file === 0) {
        toast.success("Everything's already on durable storage. Nothing to do.");
      } else if (skipped_missing_file > 0) {
        toast.warning(
          `Migrated ${migrated} · Already durable ${already_migrated} · ` +
          `${skipped_missing_file} file${skipped_missing_file === 1 ? "" : "s"} were already lost — re-upload them.`,
        );
      } else {
        toast.success(
          `Migrated ${migrated} attachment${migrated === 1 ? "" : "s"}. ` +
          `${already_migrated} were already durable.`,
        );
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Migration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="glass-card" data-testid={`${cfg.testid}-card`}>
      <CardContent className="p-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
          <HardDriveDownload className="w-5 h-5 text-amber-500" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-lg">{cfg.title}</h3>
          <p className="text-sm text-muted-foreground mt-1">{cfg.description}</p>
          {result && (
            <p
              className="text-xs text-muted-foreground mt-2 font-mono"
              data-testid={`${cfg.testid}-result`}
            >
              Migrated {result.migrated} · Already durable {result.already_migrated} · Missing {result.skipped_missing_file} · Scanned {result[cfg.scannedKey]} {cfg.scannedLabel}
            </p>
          )}
        </div>
        <Button
          onClick={handleMigrate}
          disabled={busy}
          data-testid={`${cfg.testid}-btn`}
          className="shrink-0"
        >
          {busy ? "Migrating…" : "Run migration"}
        </Button>
      </CardContent>
    </Card>
  );
};

export default MigrateAttachmentsCard;
